import type { Decimal, MasterAddress, MemberCardOption, MemberRecipient, MemberTopUp, ToCardEstimate } from '@/api/masterAccount';
import type { MoneyRequestState, MoneyRequestView } from '@/api/moneyRequests';
import type { Ticket } from '@/api/support';
import { decimalValue, toCardAmount, type ChipTone } from '@/views/cabinet/master/masterUi';
import { errorCodeOf } from '@/views/vitrine/concierge/conciergeUi';

/**
 * Pure rules of «Пополнить карту близкого» and «Попросить» (docs/cabinet-redesign/07 §4.10, owner answers §0.1): which
 * card of a member gets the money, a request's chip and buttons, the thread title, the estimate's card-price line.
 * Functions return i18n KEYS and their parameters, never rendered text — `t()` belongs to the component.
 */

/** A translatable line: the key and its named parameters. */
export interface Line {
  key: string;
  params?: Record<string, string>;
}

// ─── «Пополнить карту близкого» ─────────────────────────────────────────────────

/**
 * The member's card a top-up goes to: the rail's top-up card when it can receive, else the first card that can; null
 * when none can (the member is shown, but not selectable).
 */
export function recipientCard(r: Pick<MemberRecipient, 'cards'>): MemberCardOption | null {
  return r.cards.find(c => c.topUpCard && c.canReceive) ?? r.cards.find(c => c.canReceive) ?? null;
}

/** The members a top-up can reach, in the server's order; the ones whose cards cannot receive come last. */
export function sortRecipients(list: ReadonlyArray<MemberRecipient>): MemberRecipient[] {
  return [...list].sort((a, b) => Number(!!recipientCard(b)) - Number(!!recipientCard(a)));
}

/** At least one member's card can receive — the «Пополнить карту близкого» entry is worth showing. */
export function hasReachableRecipient(list: ReadonlyArray<MemberRecipient>): boolean {
  return list.some(r => recipientCard(r) !== null);
}

/** «·· 1234» of a card, or an empty string while its number is unknown (a card not issued yet). */
export function last4Label(lastFour: string | null | undefined): string {
  return lastFour ? `·· ${lastFour}` : '';
}

/**
 * The subtitle of a member in the picker: their card («·· 1234»), «карта выпускается» for a card not issued yet (the
 * gift pays its price first), or «карта не принимает пополнения».
 */
export function recipientSubtitle(r: Pick<MemberRecipient, 'cards'>): Line {
  const card = recipientCard(r);
  if (!card) return { key: 'cabinet.member.cannotReceive' };
  if (card.status === 'REQUESTED' || !card.lastFour) return { key: 'cabinet.member.cardIssuing' };
  return { key: 'cabinet.member.cardLine', params: { last4: card.lastFour } };
}

/**
 * «Из них N — выпуск карты»: the part of a member's gift that pays their unpaid card first (§0.1 item 5), shown in the
 * estimate; null when nothing goes to the card price.
 */
export function cardPriceLine(e: Pick<ToCardEstimate, 'cardPrice'>, format: (v: Decimal) => string): Line | null {
  if (e.cardPrice === null || e.cardPrice === undefined || decimalValue(e.cardPrice) <= 0) return null;
  return { key: 'cabinet.member.ofWhichCard', params: { amount: format(e.cardPrice) } };
}

// ─── money requests ─────────────────────────────────────────────────────────────

/** The DsStatusChip tone of a request: waiting — pending; on the card — done; refused or failed — blocked. */
export function requestTone(state: MoneyRequestState): ChipTone {
  switch (state) {
    case 'PENDING':
    case 'APPROVED': return 'pending';
    case 'CREDITED': return 'done';
    case 'DECLINED':
    case 'FAILED': return 'blocked';
    case 'EXPIRED':
    case 'CANCELLED': return 'neutral';
  }
  return 'neutral';
}

/**
 * The chip's i18n key. A withdrawn request reads «Отменён»; replaced by a newer one — «Заменён новым»; the payer who
 * muted the requester sees «Запросы отключены» (the requester never learns it, the server sends WITHDRAWN to them).
 */
export function requestStateKey(r: Pick<MoneyRequestView, 'state' | 'closeReason' | 'side'>): string {
  if (r.state === 'CANCELLED') {
    if (r.closeReason === 'REPLACED') return 'cabinet.money.state.REPLACED';
    if (r.closeReason === 'MUTED' && r.side === 'PAYER') return 'cabinet.money.state.MUTED';
  }
  return `cabinet.money.state.${r.state}`;
}

/** The buttons of a request's card, by the server's flags and the viewer's side. */
export interface RequestButtons {
  approve: boolean;
  decline: boolean;
  cancel: boolean;
  /** «Не получать запросы от …» — the payer, while the requester is not muted. */
  mute: boolean;
  /** «Снова получать запросы» — the payer who muted them. */
  unmute: boolean;
}

export function requestButtons(r: Pick<MoneyRequestView, 'side' | 'canApprove' | 'canDecline' | 'canCancel'>,
                               muted: boolean): RequestButtons {
  const payer = r.side === 'PAYER';
  return {
    approve: payer && r.canApprove,
    decline: payer && r.canDecline,
    cancel: r.side === 'REQUESTER' && r.canCancel,
    mute: payer && !muted,
    unmute: payer && muted,
  };
}

/** The other member of the request as the viewer names them. */
export function counterpart(r: Pick<MoneyRequestView, 'side' | 'requester' | 'payer'>): { id: string; name: string | null } {
  const p = r.side === 'PAYER' ? r.requester : r.payer;
  return { id: p.customerId, name: p.name };
}

/**
 * The card's headline: the payer reads «Анна Иванова просит», the requester «Ваш запрос · Иван Петров» — no declined
 * case of a full name is needed in any language.
 */
export function requestTitle(r: Pick<MoneyRequestView, 'side' | 'requester' | 'payer'>, unknownName: string): Line {
  const name = counterpart(r).name ?? unknownName;
  return r.side === 'PAYER'
    ? { key: 'cabinet.money.titleFrom', params: { name } }
    : { key: 'cabinet.money.titleTo', params: { name } };
}

/**
 * The title of a MONEY_REQUEST thread in «Переписки»: the request's headline when the request is known (by the
 * thread's `refId`), else null — the caller falls back to the plain «Запрос суммы».
 */
export function moneyThreadTitle(th: Pick<Ticket, 'refType' | 'refId'>,
                                 requests: ReadonlyMap<string, MoneyRequestView>, unknownName: string): Line | null {
  if (th.refType !== 'MONEY_REQUEST' || !th.refId) return null;
  const r = requests.get(th.refId);
  return r ? requestTitle(r, unknownName) : null;
}

/** The latest requests worth a line on the home: those still waiting first, then the newest; at most `limit`. */
export function homeRequests(list: ReadonlyArray<MoneyRequestView>, limit = 3): MoneyRequestView[] {
  const at = (r: MoneyRequestView) => Date.parse(r.createdAt) || 0;
  return [...list]
    .sort((a, b) => Number(b.state === 'PENDING') - Number(a.state === 'PENDING') || at(b) - at(a))
    .slice(0, limit);
}

/** How many requests wait for MY answer — the badge of the home entry. */
export function waitingForMe(list: ReadonlyArray<MoneyRequestView>): number {
  return list.filter(r => r.side === 'PAYER' && r.state === 'PENDING').length;
}

/** The result of checking the amount the payer types into «Разрешить». */
export type ApprovalAmount =
  | { ok: true; amount: string; partial: boolean }
  | { ok: false; reason: 'invalid' | 'above' | 'insufficient' };

/**
 * The approved amount: empty input — the requested one; otherwise a positive amount of at most two decimals, never
 * above the request (§0.1 item 8 — only less) and — when `available` (the chosen address's free money, from the
 * server) is known — never above what the shared balance holds (§4.3 item 3).
 */
export function approvalAmount(input: string, requested: Decimal, available?: Decimal | null): ApprovalAmount {
  const a = input.trim() ? toCardAmount(input) : String(requested);
  if (!a) return { ok: false, reason: 'invalid' };
  if (Number(a) > decimalValue(requested) + 1e-9) return { ok: false, reason: 'above' };
  if (available !== null && available !== undefined && Number(a) > decimalValue(available) + 1e-9) {
    return { ok: false, reason: 'insufficient' };
  }
  return { ok: true, amount: a, partial: Number(a) < decimalValue(requested) - 1e-9 };
}

/**
 * What «Разрешить» can offer against the shared balance (docs/cabinet-redesign/07 §4.3 item 3): `full` — the
 * requested amount is there; `partial` — less is there, offer «Разрешить N из M» with N = what is there (two decimals,
 * rounded down); `none` — not even the «to card» minimum is there, offer «Пополнить общий баланс». `available` is the
 * richest address in the request's coin (the server's figure); null while unknown — then the plain «Разрешить».
 */
export type ApprovalPlan =
  | { kind: 'unknown' }
  | { kind: 'full'; amount: string; available: number }
  | { kind: 'partial'; amount: string; available: number }
  | { kind: 'none'; available: number };

export function approvalPlan(requested: Decimal, available: Decimal | null | undefined,
                             minimum: Decimal | null | undefined): ApprovalPlan {
  if (available === null || available === undefined) return { kind: 'unknown' };
  const have = decimalValue(available);
  const want = decimalValue(requested);
  if (have + 1e-9 >= want) return { kind: 'full', amount: String(requested), available: have };
  const floored = Math.floor(have * 100 + 1e-6) / 100;
  const min = minimum === null || minimum === undefined ? 0 : decimalValue(minimum);
  if (floored <= 0 || floored + 1e-9 < min) return { kind: 'none', available: have };
  return { kind: 'partial', amount: floored.toFixed(2), available: have };
}

/** The payer's open master addresses that can pay a request in `asset` — the most money first. */
export function approveSources(addresses: ReadonlyArray<MasterAddress>, asset: string): MasterAddress[] {
  return addresses
    .filter(a => a.asset.toUpperCase() === asset.toUpperCase())
    .sort((a, b) => decimalValue(b.available) - decimalValue(a.available));
}

// ─── members' top-ups in the history ────────────────────────────────────────────

/** The chip tone of a member's card top-up. */
export function memberTopUpTone(m: Pick<MemberTopUp, 'state'>): ChipTone {
  switch (m.state) {
    case 'IN_PROGRESS': return 'pending';
    case 'CREDITED': return 'done';
    case 'CANCELLED':
    case 'RETURNED': return 'neutral';
  }
  return 'neutral';
}

/** The chip's key: «В пути», «Зачислено», «Отменено», «Возвращается» (to the sender: «на общий баланс»). */
export function memberTopUpStateKey(m: Pick<MemberTopUp, 'state'>): string {
  return `cabinet.member.history.${m.state}`;
}

/** The row's title: «Иван Петров · на карту ··1234» for what I sent, «Анна Иванова · пополнение вашей карты» for what I got. */
export function memberTopUpTitle(m: Pick<MemberTopUp, 'direction' | 'counterpartName' | 'cardLastFour'>,
                                 unknownName: string): Line {
  const name = m.counterpartName ?? unknownName;
  if (m.direction === 'INCOMING') return { key: 'cabinet.member.history.incoming', params: { name } };
  return m.cardLastFour
    ? { key: 'cabinet.member.history.outgoing', params: { name, last4: m.cardLastFour } }
    : { key: 'cabinet.member.history.outgoingNoCard', params: { name } };
}

/** Sent money leaves the balance, received money lands on the card — the row's sign. */
export function memberTopUpSign(m: Pick<MemberTopUp, 'direction'>): 1 | -1 {
  return m.direction === 'INCOMING' ? 1 : -1;
}

/**
 * The top-up did not move the money in the end: CANCELLED before the hop (the recipient left the group, the operator
 * cancelled) or RETURNED to the sender's shared balance. Neither side's row is then a debit or a credit — the amount is
 * shown unsigned and struck through, beside the «Отменено» / «Возвращается» chip.
 */
export function memberTopUpVoid(m: Pick<MemberTopUp, 'state'>): boolean {
  return m.state === 'CANCELLED' || m.state === 'RETURNED';
}

/** What the row shows as the amount: what reached the card once known (incoming), else what left the balance. */
export function memberTopUpAmount(m: Pick<MemberTopUp, 'direction' | 'amount' | 'credited'>): Decimal | null {
  if (m.direction === 'INCOMING' && m.credited !== null && m.credited !== undefined) return m.credited;
  return m.amount;
}

/**
 * A refusal that only means «this card is out of date» — the request was answered or expired meanwhile: re-read it, no
 * message. Every other refusal of «Разрешить» is a reason the payer must read, 409s included: not enough on the shared
 * balance (MASTER_INSUFFICIENT), the card cannot receive — a blanket «409 = stale» swallowed them, and «Разрешить»
 * silently did nothing (E2E family office, 31.10).
 */
export function isStaleRequestRefusal(e: unknown): boolean {
  const code = errorCodeOf(e);
  return code === 'MONEY_REQUEST_NOT_PENDING' || code === 'MONEY_REQUEST_EXPIRED';
}

// ─── why it closed without money (E2E family office Б-3) ─────────────────────────

/**
 * The codes the server gives for a member's top-up cancelled before its hop (`MemberTopUp.cancelReason`) and for a
 * request whose top-up did not reach the card (`MoneyRequestView.closeReason` of a FAILED one) → their i18n keys.
 * An unknown code shows nothing rather than a raw code.
 */
const CLOSE_REASON_KEYS: Readonly<Record<string, string>> = {
  RECIPIENT_LEFT_GROUP: 'cabinet.member.reason.RECIPIENT_LEFT_GROUP',
  TOPUP_CARD_CANNOT_RECEIVE: 'cabinet.member.reason.TOPUP_CARD_CANNOT_RECEIVE',
  TO_CARD_WAITING_TIMEOUT: 'cabinet.member.reason.TO_CARD_WAITING_TIMEOUT',
  TO_CARD_CANCELLED_BY_SUPPORT: 'cabinet.member.reason.TO_CARD_CANCELLED_BY_SUPPORT',
};

export function closeReasonKey(code: string | null | undefined): string | null {
  return code ? CLOSE_REASON_KEYS[code] ?? null : null;
}

/** «Участник вышел из семьи» under a cancelled member top-up, for both sides; null otherwise. */
export function memberTopUpReasonKey(m: Pick<MemberTopUp, 'state' | 'cancelReason'>): string | null {
  return m.state === 'CANCELLED' ? closeReasonKey(m.cancelReason) : null;
}

/** Why an approved request did not reach the card, on both sides' cards; null for any other state. */
export function requestFailureReasonKey(r: Pick<MoneyRequestView, 'state' | 'closeReason'>): string | null {
  return r.state === 'FAILED' ? closeReasonKey(r.closeReason) : null;
}

// ─── the payer's thread chips, one per requester (E2E family office Б-6) ─────────

/** A chip of «Переписки»: one thread, or the money-request threads of one counterpart and side together. */
export type ThreadChip<T extends Pick<Ticket, 'id' | 'refType' | 'refId'>> =
  | { kind: 'thread'; key: string; thread: T }
  | { kind: 'money'; key: string; title: Line; threads: T[] };

/**
 * Every money request has its own thread, so a payer asked seven times by one member read seven identical «Анна
 * Петрова просит» chips. The chips group them: one chip per counterpart and side, in the place of its first thread,
 * its threads newest request first (by the request's creation); a group of one stays a plain thread chip. Threads of
 * a request not known yet stay single.
 */
export function groupThreadChips<T extends Pick<Ticket, 'id' | 'refType' | 'refId'>>(
  threads: ReadonlyArray<T>, requests: ReadonlyMap<string, MoneyRequestView>, unknownName: string): ThreadChip<T>[] {
  const out: ThreadChip<T>[] = [];
  const groups = new Map<string, { key: string; title: Line; threads: T[] }>();
  for (const th of threads) {
    const r = th.refType === 'MONEY_REQUEST' && th.refId ? requests.get(th.refId) : undefined;
    if (!r) {
      out.push({ kind: 'thread', key: th.id, thread: th });
      continue;
    }
    const gk = `money:${r.side}:${counterpart(r).id}`;
    let g = groups.get(gk);
    if (!g) {
      g = { key: gk, title: requestTitle(r, unknownName), threads: [] };
      groups.set(gk, g);
      out.push({ kind: 'money', key: gk, title: g.title, threads: g.threads });
    }
    g.threads.push(th);
  }
  const created = (th: T) => Date.parse(requests.get(th.refId ?? '')?.createdAt ?? '') || 0;
  return out.map(c => {
    if (c.kind !== 'money') return c;
    if (c.threads.length === 1) return { kind: 'thread', key: c.threads[0].id, thread: c.threads[0] };
    c.threads.sort((a, b) => created(b) - created(a));
    return c;
  });
}
